import { add, listen, qs, remove, toggle } from "@fluorescent/dom";
import { getSizedImageUrl } from "@shopify/theme-images";
import Delegate from "ftdomdelegate";
import { on } from "evx";
import { disableBodyScroll, enableBodyScroll } from "body-scroll-lock";
import { createFocusTrap } from "focus-trap";
import { renderUnitPrice } from "@/lib/unit-pricing";

import { cart, formatMoney, getProduct } from "@/neon";

import { handleForm } from "@/lib/quick-add";
import svg from "@/lib/svg";

import { dispatchCustomEvent, CustomEvents } from "@/lib/dispatch-custom-event";
export default function QuickCart(node) {
  const delegate = new Delegate(node);
  const overlay = qs("[data-overlay]", node);

  const cartTab = qs("[data-cart]", node);
  const configureTab = qs("[data-configure]", node);

  // Cart
  const itemsContainer = qs("[data-items]", node);
  const empty = qs("[data-empty]", node);
  const footer = qs("[data-footer]", node);
  const discounts = qs("[data-discounts]", footer);
  const subtotal = qs("[data-subtotal]", footer);

  // Configure
  const addContainer = qs("[data-add]", configureTab);
  const contents = qs("[data-contents]", addContainer);

  delegate.on("click", "button[data-decrease]", (_, target) => {
    const qty = parseInt(qs("[data-qty]", target.parentNode).innerHTML) - 1;
    cart.updateItem(target.dataset.decrease, qty);
  });

  delegate.on("click", "button[data-increase]", (_, target) => {
    const qty = parseInt(qs("[data-qty]", target.parentNode).innerHTML) + 1;
    cart.updateItem(target.dataset.increase, qty);
  });

  delegate.on("click", ".quick-cart__item-remove", (_, target) => {
    cart.updateItem(target.dataset.itemId, 0);
  });

  const cartTrap = createFocusTrap(cartTab, {
    allowOutsideClick: true,
    escapeDeactivates: false,
  });

  const configureTrap = createFocusTrap(configureTab, {
    allowOutsideClick: true,
    escapeDeactivates: false,
  });

  // Initial cart fetch
  cart.get().then(renderCart);

  // On every update
  on("cart:updated", ({ cart }) => renderCart(cart));
  dispatchCustomEvent(CustomEvents.cartUpdated, { cart: cart });

  const overlayClick = listen(overlay, "click", close);
  const closeClick = listen(qs("[data-close]", node), "click", close);
  const keyDown = listen(node, "keydown", checkEscape);

  on("cart:open", (_, { flash }) => open(flash));

  on("cart:configureLoading", () => {
    open(false, true);
    remove(cartTab, "visible");
    add(configureTab, "visible");
  });

  on("cart:configureReady", (_, { html, handle }) => {
    const product = getProduct(handle);
    product(data => {
      remove(addContainer, "loading");
      contents.innerHTML = html;

      handleForm(data, configureTab);
      configureTrap.activate();
      dispatchCustomEvent(CustomEvents.quickViewLoaded);
    });
  });

  function open(variant, configure) {
    add(node, "active");
    setTimeout(() => {
      disableBodyScroll(node, {
        allowTouchMove: el => {
          while (el && el !== document.body) {
            if (el.getAttribute("data-scroll-lock-ignore") !== null) {
              return true;
            }
            el = el.parentNode;
          }
        },
        reserveScrollBarGap: true,
      });
      add(node, "visible");

      dispatchCustomEvent(CustomEvents.quickCartOpen);
      toggle(configureTab, "visible", configure === true);
      toggle(cartTab, "visible", !configure);

      !configure && cartTrap.activate();

      // Flash a product when opening
      if (variant) {
        add(qs(`[data-id="${variant}"]`, node), "flash");
        setTimeout(() => {
          remove(qs(`[data-id="${variant}"]`, node), "flash");
        }, 2000);
      }
    }, 50);
  }

  function close() {
    remove(node, "visible");
    setTimeout(() => {
      remove(node, "active");
      enableBodyScroll(node);

      remove(configureTab, "visible");
      add(addContainer, "loading");
      add(cartTab, "visible");

      cartTrap.deactivate();
      configureTrap.deactivate();
      dispatchCustomEvent(CustomEvents.quickCartClose);
    }, 350);
  }

  function checkEscape({ keyCode }) {
    if (keyCode === 27) close();
  }

  function renderCart(cart) {
    const { cart_level_discount_applications: cartDiscounts } = cart;
    itemsContainer.innerHTML = renderItems(cart);
    discounts.innerHTML = renderCartDiscounts(cartDiscounts);

    toggle(footer, "visible", cart.sorted.length);
    toggle(empty, "visible", !cart.sorted.length);
    toggle(discounts, "visible", cartDiscounts.length);
    if (subtotal) {
      subtotal.innerHTML = formatMoney(cart.total_price);
    }
  }

  function renderItems({ sorted }) {
    return r(
      sorted.length > 0,
      sorted.reduce((markup, item) => (markup += createItem(item)), "")
    );
  }

  function destroy() {
    overlayClick();
    keyDown();
    closeClick();
  }

  return { open, close, destroy };
}

function createItem({ line_level_discount_allocations: discounts, ...item }) {
  const imgSrc = item.featured_image ? item.featured_image.url : item.image;
  const imgAlt = item.featured_image ? item.featured_image.alt : "";
  const imgUrl = imgSrc && getSizedImageUrl(imgSrc, "120x");
  const image = r(
    imgSrc,
    `<img class="image__img lazyload" alt="${imgAlt}" data-src="${imgUrl}" />`
  );

  const sellingPlanName = item.selling_plan_allocation
    ? `<p class="fs-body-small c-subdued">${item.selling_plan_allocation.selling_plan.name}</p>`
    : ``;

  return `
    <div class="quick-cart__item ff-body" data-id="${item.variant_id}">
      <div class="quick-cart__item-left">
        <a href="${item.url}">
          <div class="quick-cart__image">${image}</div>
        </a>
        <div class="quick-cart__control">
          <button class="quick-cart__button" data-decrease="${
            item.variant_id
          }" href="#">
            ${svg.remove}
          </button>
          <div class="quick-cart__qty ff-body ta-c" data-qty>${
            item.quantity
          }</div>
          <button class="quick-cart__button" data-increase="${
            item.variant_id
          }" href="#">
            ${svg.add}
          </button>
        </div>
      </div>
      <div class="quick-cart__item-right">
        <h4><a href="${item.url}">${item.product_title}</a></h4>
        <div>
          ${r(
            item.original_price > item.final_price,
            `<s class="qty">${formatMoney(item.original_price)}</s>`
          )}
          ${formatMoney(item.final_price)}
          ${r(
            item.quantity > 1,
            `<span class="c-subdued">x ${item.quantity}</span>`
          )}
        </div>
        ${renderOptions(item)}
        ${renderLineDiscounts(discounts)}
        ${renderUnitPrice(item.unit_price, item.unit_price_measurement)}
        ${sellingPlanName}
        <a role="button" href="#" class="quick-cart__item-remove" data-item-id="${
          item.variant_id
        }">${window.theme.strings.cart.remove}</a>
      </div>
    </div>
  `;
}

function renderOptions({ options_with_values: options, variant_title }) {
  return r(
    options.length > 0 && variant_title,
    options.reduce(
      (markup, { name, value }) => markup + `<div>${name}: ${value}</div>`,
      ""
    )
  );
}

function renderCartDiscounts(discounts) {
  return r(
    Boolean(discounts.length),
    `
      <ul>
        ${discounts.map(
          ({ title, total_allocated_amount: value }) =>
            `<div>${title} (-${formatMoney(value)})</div>`
        )}
      </ul>
    `
  );
}

function renderLineDiscounts(discounts) {
  const formatted_discounts = discounts.map(
    ({ amount, discount_application: { title } }) => {
      return `<li>${title} (-${formatMoney(amount)})</li>`;
    }
  );

  return r(
    Boolean(discounts.length),
    `<ul class="quick-cart__item-discounts c-subdued">${formatted_discounts}</ul>`
  );
}

function r(bool, whenTrue) {
  return bool ? whenTrue : ``;
}
